import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { apiClient } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Loader2, Check, Clock, Trophy, Users, Zap } from 'lucide-react';

const FreeTrial = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [alreadyTrialed, setAlreadyTrialed] = useState(null);
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
  });

  const SUBSCRIBE_URL = 'https://rxgrit.buildnutrition.ca/signup';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setAlreadyTrialed(null);

    if (form.password !== form.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (form.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    if (!form.phone || form.phone.trim().length < 10) {
      toast.error('Please enter a valid phone number');
      return;
    }

    setLoading(true);
    try {
      localStorage.clear();
      sessionStorage.clear();

      const res = await apiClient.post('/auth/trial-register', {
        name: form.name,
        email: form.email,
        phone: form.phone,
        password: form.password,
      });

      localStorage.setItem('rxgrit_token', res.data.token);
      localStorage.setItem('rxgrit_user', JSON.stringify(res.data.user));

      toast.success('Welcome to your free trial!');

      setTimeout(() => {
        window.location.replace('/my-actions');
      }, 500);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Registration failed';
      if (typeof detail === 'string' && (detail.startsWith('ALREADY_TRIALED:') || detail.startsWith('PHONE_ALREADY_TRIALED:'))) {
        const matchedOn = detail.startsWith('PHONE_ALREADY_TRIALED:') ? 'phone' : 'email';
        const clean = detail.replace(/^ALREADY_TRIALED:|^PHONE_ALREADY_TRIALED:/, '');
        setAlreadyTrialed({ message: clean, matchedOn });
        toast.error('You have already used your free trial.');
      } else if (typeof detail === 'string' && detail.startsWith('SUBSCRIPTION_LAPSED:')) {
        setAlreadyTrialed({ message: detail.replace('SUBSCRIPTION_LAPSED:', ''), matchedOn: 'renewal' });
        toast.error('Your subscription is inactive.');
      } else {
        toast.error(detail);
      }
    } finally {
      setLoading(false);
    }
  };

  const inputCls = 'bg-white border-stone-300 focus:border-[#EFB342] focus:ring-1 focus:ring-[#EFB342] text-stone-900 placeholder:text-stone-400 mt-1 h-11';
  const labelCls = 'text-stone-700 text-sm font-medium';

  const benefits = [
    { icon: Check, title: 'Daily check-ins', body: 'Track your 10 daily practices and earn points for every action you complete.' },
    { icon: Trophy, title: 'Compete & earn badges', body: 'See how you stack up on the leaderboard and unlock badges for your progress.' },
    { icon: Zap, title: 'Daily GRIT tips', body: 'Menopause-aware nutrition insights delivered daily to keep you motivated and informed.' },
    { icon: Users, title: 'Supportive community', body: 'Connect with other women on the same journey through the members-only feed.' },
  ];

  return (
    <div className="min-h-screen bg-[#F5F4F1] flex flex-col text-stone-900">
      {/* Header */}
      <header className="p-4 sm:p-6 border-b border-stone-200 bg-[#F5F4F1]/95 backdrop-blur">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="trial-brand-logo">
            <div className="w-9 h-9 bg-[#EFB342] rounded-lg flex items-center justify-center">
              <span className="text-black font-heading font-bold text-base leading-none">Rx</span>
            </div>
            <div className="leading-tight">
              <span className="font-heading font-bold text-stone-900 text-base block">RxGRIT</span>
              <span className="text-stone-500 text-[10px] uppercase tracking-widest">by BUILD Nutrition</span>
            </div>
          </Link>
          <Link to="/login" className="text-stone-600 hover:text-stone-900 text-sm font-medium" data-testid="trial-login-link">
            Already have an account? Log in
          </Link>
        </div>
      </header>

      <div className="flex-1 flex items-center justify-center p-4 sm:p-8">
        <div className="w-full max-w-5xl grid lg:grid-cols-2 gap-10 lg:gap-16 items-start py-6">
          {/* Left side - Benefits */}
          <div className="space-y-8">
            <div>
              <p className="text-[#EFB342] uppercase tracking-[0.2em] text-xs font-semibold mb-3">7-Day Free Trial</p>
              <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-stone-900 mb-4 leading-[1.05] uppercase tracking-tight">
                Try RxGRIT <span className="text-[#EFB342]">free for 7 days.</span>
              </h1>
              <p className="text-stone-600 text-base md:text-lg leading-relaxed max-w-lg">
                Menopause brings changes you didn&apos;t ask for. But you can tackle them with knowledge, consistency, and perseverance — that&apos;s GRIT in action.
              </p>
              <p className="text-stone-500 text-sm md:text-base mt-3">No credit card required.</p>
            </div>

            <div className="space-y-3">
              {benefits.map(({ icon: Icon, title, body }) => (
                <div key={title} className="flex items-start gap-4 p-4 bg-white rounded-2xl border border-stone-200">
                  <div className="w-10 h-10 bg-[#EFB342]/15 border border-[#EFB342]/25 rounded-full flex items-center justify-center flex-shrink-0">
                    <Icon className="text-[#B8871E]" size={18} />
                  </div>
                  <div>
                    <h3 className="font-heading font-bold text-stone-900 text-base mb-1 leading-tight">{title}</h3>
                    <p className="text-stone-600 text-sm leading-relaxed">{body}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex items-start gap-3 p-4 bg-[#EFB342]/10 rounded-2xl border border-[#EFB342]/25">
              <Clock className="text-[#B8871E] flex-shrink-0 mt-0.5" size={22} />
              <p className="text-stone-700 text-sm leading-relaxed">
                <strong className="text-stone-900">Your points count.</strong> Any points you earn during your trial are kept if you sign up for full access before the 7 days are up.
              </p>
            </div>
          </div>

          {/* Right side - Form */}
          <div className="bg-white border border-stone-200 rounded-3xl p-6 sm:p-8 shadow-sm lg:sticky lg:top-8">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-stone-900 mb-6 uppercase tracking-tight">Create your free account</h2>

            {alreadyTrialed && (
              <div
                className="mb-6 rounded-2xl border border-[#EFB342]/40 bg-[#EFB342]/10 p-4"
                data-testid="already-trialed-banner"
              >
                <p className="text-stone-800 text-sm mb-3 leading-relaxed">
                  <strong className="block text-stone-900 mb-1">
                    {alreadyTrialed.matchedOn === 'phone'
                      ? 'This phone number has already claimed a free trial.'
                      : alreadyTrialed.matchedOn === 'renewal'
                        ? 'Your subscription is inactive.'
                        : "You've already used your free 7-day trial."}
                  </strong>
                  {alreadyTrialed.message}
                </p>
                <a
                  href={SUBSCRIBE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="already-trialed-subscribe-btn"
                  className="inline-flex items-center gap-2 rounded-full bg-[#EFB342] px-4 py-2 text-sm font-semibold text-black hover:bg-[#D99F30] transition-colors"
                >
                  Subscribe now
                </a>
                <Link
                  to="/login"
                  data-testid="already-trialed-login-link"
                  className="ml-3 text-sm text-stone-700 underline hover:text-stone-900"
                >
                  Or log in
                </Link>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label className={labelCls}>Full name</Label>
                <Input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className={inputCls}
                  placeholder="Jane Doe"
                  required
                  data-testid="trial-name-input"
                />
              </div>

              <div>
                <Label className={labelCls}>Email</Label>
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className={inputCls}
                  placeholder="jane@example.com"
                  required
                  data-testid="trial-email-input"
                />
              </div>

              <div>
                <Label className={labelCls}>Phone number</Label>
                <Input
                  type="tel"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className={inputCls}
                  placeholder="(555) 123-4567"
                  required
                  data-testid="trial-phone-input"
                />
              </div>

              <div>
                <Label className={labelCls}>Password</Label>
                <Input
                  type="password"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  className={inputCls}
                  placeholder="••••••••"
                  required
                  data-testid="trial-password-input"
                />
              </div>

              <div>
                <Label className={labelCls}>Confirm password</Label>
                <Input
                  type="password"
                  value={form.confirmPassword}
                  onChange={(e) => setForm({ ...form, confirmPassword: e.target.value })}
                  className={inputCls}
                  placeholder="••••••••"
                  required
                  data-testid="trial-confirm-password-input"
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[#EFB342] text-black hover:bg-[#D99F30] font-bold uppercase tracking-wider py-6 text-base rounded-full mt-4"
                data-testid="trial-submit-btn"
              >
                {loading ? <Loader2 className="animate-spin mr-2" size={20} /> : null}
                Start my free trial
              </Button>

              <p className="text-xs text-stone-500 text-center mt-4 leading-relaxed">
                By signing up, you agree to our Terms of Service and Privacy Policy. No credit card required. This is a{' '}
                <strong className="text-stone-700">one-time 7-day trial offer</strong>.
              </p>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FreeTrial;
